import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Code as CodeIcon,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Strikethrough,
  Undo,
  Redo,
} from "lucide-react";

/**
 * Lightweight TipTap-powered rich text editor used by the Tasks module
 * (description field on TaskFormDialog + TaskDetailDrawer).
 *
 * - Always emits sanitized HTML on change (`onChange(html)`).
 * - Backwards-compatible with plain-text descriptions: a string with no
 *   tags is wrapped into a single <p> on load so existing tasks still
 *   render the way the user wrote them.
 * - Read-only mode (`readOnly`) renders the content as static HTML
 *   (still sanitized) without the toolbar, suitable for the drawer's
 *   inline rendering after blur.
 */

const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "u", "s", "code", "pre",
    "blockquote", "h1", "h2", "h3", "h4", "ul", "ol", "li", "a",
  ],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};

export function sanitizeRichText(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), SANITIZE_OPTS);
}

/** Treat a string as plain text if it has no tag markers at all. */
function normalizeIncoming(value) {
  if (value === undefined || value === null) return "";
  const v = String(value);
  if (!v) return "";
  if (v.includes("<") && v.includes(">")) return v;
  // Plain text — preserve line breaks by converting them to <br>.
  const escaped = v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p>${escaped.replace(/\n/g, "<br/>")}</p>`;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Start typing…",
  testId,
  minHeight = 120,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: normalizeIncoming(value),
    onUpdate: ({ editor: e }) => {
      // sanitize before bubbling to parent so persisted HTML is safe.
      onChange && onChange(sanitizeRichText(e.getHTML()));
    },
    editorProps: {
      attributes: {
        class:
          "rt-editor prose prose-sm max-w-none focus:outline-none min-h-[" +
          minHeight +
          "px] px-3 py-2",
        "data-testid": testId || "rich-text-editor",
      },
    },
  });

  // External value updates (e.g. when opening a new task) — only push
  // back into the editor when the difference is real, otherwise we'd
  // wipe the user's in-progress cursor on every render.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = normalizeIncoming(value);
    if (current !== incoming && incoming !== "<p></p>") {
      editor.commands.setContent(incoming || "", false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="bloq-input min-h-[120px] text-sm text-[#82828c]">
        Loading editor…
      </div>
    );
  }

  const Btn = ({ on, action, label, children, testid }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        action();
      }}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`rt-toolbar-btn ${on ? "rt-toolbar-btn--on" : ""}`}
    >
      {children}
    </button>
  );

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rt-wrapper border border-[#1a1a1f]/15 bg-white" data-testid={testId ? `${testId}-wrapper` : "rich-text-wrapper"}>
      <div className="rt-toolbar flex flex-wrap gap-0.5 px-2 py-1.5 border-b border-[#1a1a1f]/10 bg-[#f5efe6]/60">
        <Btn on={editor.isActive("bold")} action={() => editor.chain().focus().toggleBold().run()} label="Bold" testid="rt-bold"><Bold className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("italic")} action={() => editor.chain().focus().toggleItalic().run()} label="Italic" testid="rt-italic"><Italic className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("strike")} action={() => editor.chain().focus().toggleStrike().run()} label="Strikethrough" testid="rt-strike"><Strikethrough className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("code")} action={() => editor.chain().focus().toggleCode().run()} label="Inline code" testid="rt-code"><CodeIcon className="w-3.5 h-3.5" /></Btn>
        <span className="rt-sep" />
        <Btn on={editor.isActive("heading", { level: 2 })} action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading 2" testid="rt-h2"><Heading2 className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("heading", { level: 3 })} action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Heading 3" testid="rt-h3"><Heading3 className="w-3.5 h-3.5" /></Btn>
        <span className="rt-sep" />
        <Btn on={editor.isActive("bulletList")} action={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list" testid="rt-ul"><List className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("orderedList")} action={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list" testid="rt-ol"><ListOrdered className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("blockquote")} action={() => editor.chain().focus().toggleBlockquote().run()} label="Blockquote" testid="rt-quote"><Quote className="w-3.5 h-3.5" /></Btn>
        <Btn on={editor.isActive("link")} action={addLink} label="Link" testid="rt-link"><LinkIcon className="w-3.5 h-3.5" /></Btn>
        <span className="rt-sep" />
        <Btn on={false} action={() => editor.chain().focus().undo().run()} label="Undo" testid="rt-undo"><Undo className="w-3.5 h-3.5" /></Btn>
        <Btn on={false} action={() => editor.chain().focus().redo().run()} label="Redo" testid="rt-redo"><Redo className="w-3.5 h-3.5" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/** Static rendering of stored HTML (sanitized). */
export function RichTextView({ html, className = "" }) {
  const safe = sanitizeRichText(html);
  if (!safe || safe === "<p></p>") {
    return (
      <p className={`text-sm text-[#82828c] italic ${className}`}>
        No description yet.
      </p>
    );
  }
  return (
    <div
      className={`rt-view prose prose-sm max-w-none ${className}`}
      data-testid="rich-text-view"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
